import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MbCard } from "@/components/MbCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  RefreshCw,
  Database,
  Shield,
  CreditCard,
  Bug
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Testing() {
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch OTP codes
  const { data: otpCodes } = useQuery({
    queryKey: ['test-otp-codes', refreshKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('otp_codes')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch payment events
  const { data: paymentEvents } = useQuery({
    queryKey: ['test-payment-events', refreshKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch audit logs
  const { data: auditLogs } = useQuery({
    queryKey: ['test-audit-logs', refreshKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profile_access_log')
        .select('*')
        .order('accessed_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch payment analytics
  const { data: paymentStats } = useQuery({
    queryKey: ['test-payment-stats', refreshKey],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('payment_events')
        .select('event_type, amount')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (error) throw error;

      const checkouts = data?.filter(e => e.event_type === 'checkout_started').length || 0;
      const successes = data?.filter(e => e.event_type === 'payment_success').length || 0;
      const failures = data?.filter(e => e.event_type === 'payment_failed').length || 0;
      const revenue = data
        ?.filter(e => e.event_type === 'payment_success')
        .reduce((sum, e) => sum + (e.amount || 0), 0) || 0;

      return {
        checkouts,
        successes,
        failures,
        revenue,
        successRate: checkouts > 0 ? ((successes / checkouts) * 100).toFixed(1) : '0',
      };
    },
  });

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const testScenarios = [
    {
      icon: Shield,
      title: "1. Forgot Password Flow",
      tests: [
        { name: "Valid phone OTP request", status: otpCodes && otpCodes.length > 0 ? "pass" : "pending" },
        { name: "Rate limiting (3/hour)", status: "manual" },
        { name: "Invalid OTP handling", status: "manual" },
        { name: "User enumeration prevention", status: "manual" },
      ],
      actions: [
        { label: "Go to Login", onClick: () => navigate('/auth') },
        { label: "View OTP Records", onClick: () => navigate('/admin/testing?tab=otp') },
      ]
    },
    {
      icon: CreditCard,
      title: "2. Payment Analytics",
      tests: [
        { name: "Test payment flow", status: paymentEvents && paymentEvents.length > 0 ? "pass" : "pending" },
        { name: "Event tracking", status: paymentEvents && paymentEvents.length > 0 ? "pass" : "pending" },
        { name: "Analytics calculation", status: paymentStats && paymentStats.successes > 0 ? "pass" : "pending" },
        { name: "Failed payment handling", status: "manual" },
      ],
      actions: [
        { label: "Go to Subscribe", onClick: () => navigate('/subscribe') },
        { label: "View Analytics", onClick: () => navigate('/admin/payment-analytics') },
      ]
    },
    {
      icon: Database,
      title: "3. Audit Logging",
      tests: [
        { name: "Bulk profile view", status: auditLogs && auditLogs.length > 0 ? "pass" : "pending" },
        { name: "Profile update logging", status: auditLogs?.some(l => l.access_type === 'update') ? "pass" : "pending" },
        { name: "IP capture", status: auditLogs?.some(l => l.ip_address) ? "pass" : "warning" },
        { name: "Unauthorized access block", status: "manual" },
      ],
      actions: [
        { label: "View Subscribers", onClick: () => navigate('/admin/subscribers') },
        { label: "View Audit Logs", onClick: () => navigate('/admin/testing?tab=audit') },
      ]
    },
    {
      icon: Bug,
      title: "4. Error Monitoring",
      tests: [
        { name: "Sentry configured", status: import.meta.env.VITE_SENTRY_DSN ? "pass" : "fail" },
        { name: "Error capture", status: "manual" },
        { name: "Edge function errors", status: "manual" },
        { name: "Payment monitoring", status: "manual" },
      ],
      actions: [
        { label: "Trigger Test Error", onClick: () => { throw new Error('Test error for Sentry'); } },
        { label: "View Sentry Dashboard", onClick: () => window.open('https://sentry.io', '_blank') },
      ]
    },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'fail': return <XCircle className="w-4 h-4 text-destructive" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default: return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="container max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Testing Dashboard</h1>
          <p className="text-muted-foreground">Validate all security features and integrations</p>
        </div>
        <Button onClick={handleRefresh} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Data
        </Button>
      </div>

      {/* Test Scenarios Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {testScenarios.map((scenario, idx) => (
          <MbCard key={idx} className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <scenario.icon className="w-6 h-6 text-primary" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{scenario.title}</h3>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              {scenario.tests.map((test, testIdx) => (
                <div key={testIdx} className="flex items-center justify-between py-1">
                  <span className="text-sm">{test.name}</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(test.status)}
                    <Badge variant={test.status === 'pass' ? 'default' : 'secondary'} className="text-xs">
                      {test.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="flex gap-2 flex-wrap">
              {scenario.actions.map((action, actionIdx) => (
                <Button
                  key={actionIdx}
                  onClick={action.onClick}
                  variant="outline"
                  size="sm"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          </MbCard>
        ))}
      </div>

      {/* Database State Viewers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* OTP Codes */}
        <MbCard className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Recent OTP Codes
          </h3>
          <ScrollArea className="h-48">
            {otpCodes && otpCodes.length > 0 ? (
              <div className="space-y-2">
                {otpCodes.map((otp: any) => (
                  <div key={otp.id} className="text-xs border-b pb-2">
                    <div className="flex justify-between">
                      <span className="font-mono">{otp.phone.slice(-4).padStart(10, '*')}</span>
                      <Badge variant={otp.verified ? "default" : "secondary"} className="text-xs">
                        {otp.verified ? 'Verified' : 'Pending'}
                      </Badge>
                    </div>
                    <div className="text-muted-foreground mt-1">
                      Attempts: {otp.attempts} | Test: {otp.is_test_otp ? 'Yes' : 'No'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No OTP codes found</p>
            )}
          </ScrollArea>
        </MbCard>

        {/* Payment Events */}
        <MbCard className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Recent Payments
          </h3>
          <ScrollArea className="h-48">
            {paymentEvents && paymentEvents.length > 0 ? (
              <div className="space-y-2">
                {paymentEvents.map((event: any) => (
                  <div key={event.id} className="text-xs border-b pb-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">{event.event_type}</span>
                      <Badge 
                        variant={event.event_type === 'payment_success' ? "default" : "secondary"}
                        className="text-xs"
                      >
                        ₹{(event.amount / 100).toFixed(0)}
                      </Badge>
                    </div>
                    {event.error_message && (
                      <div className="text-destructive mt-1">{event.error_message}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No payment events found</p>
            )}
          </ScrollArea>
        </MbCard>

        {/* Audit Logs */}
        <MbCard className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Database className="w-4 h-4" />
            Recent Audit Logs
          </h3>
          <ScrollArea className="h-48">
            {auditLogs && auditLogs.length > 0 ? (
              <div className="space-y-2">
                {auditLogs.map((log: any) => (
                  <div key={log.id} className="text-xs border-b pb-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">{log.access_type}</span>
                      <span className="text-muted-foreground">
                        {new Date(log.accessed_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-muted-foreground mt-1">
                      IP: {log.ip_address || 'Not captured'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No audit logs found</p>
            )}
          </ScrollArea>
        </MbCard>
      </div>

      {/* Payment Statistics Summary */}
      {paymentStats && (
        <MbCard className="p-6">
          <h3 className="font-semibold mb-4">Payment Analytics Summary (Last 30 Days)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Checkouts</p>
              <p className="text-2xl font-bold">{paymentStats.checkouts}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Successful</p>
              <p className="text-2xl font-bold text-green-600">{paymentStats.successes}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Failed</p>
              <p className="text-2xl font-bold text-destructive">{paymentStats.failures}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold">{paymentStats.successRate}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold">₹{(paymentStats.revenue / 100).toFixed(0)}</p>
            </div>
          </div>
        </MbCard>
      )}

      {/* Manual Testing Instructions */}
      <MbCard className="p-6 bg-muted/50">
        <h3 className="font-semibold mb-3">🎯 Manual Testing Checklist</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-medium mb-2">Forgot Password:</h4>
            <ul className="space-y-1 text-muted-foreground">
              <li>✓ Test with valid phone number</li>
              <li>✓ Verify OTP received via SMS</li>
              <li>✓ Test rate limiting (3 attempts)</li>
              <li>✓ Test with non-existent user</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Payment Flow:</h4>
            <ul className="space-y-1 text-muted-foreground">
              <li>✓ Complete test payment (4111 1111 1111 1111)</li>
              <li>✓ Verify events in database</li>
              <li>✓ Test failed payment (4000 0000 0000 0002)</li>
              <li>✓ Check analytics dashboard</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Audit Logging:</h4>
            <ul className="space-y-1 text-muted-foreground">
              <li>✓ View subscribers list</li>
              <li>✓ Update user status</li>
              <li>✓ Check audit_log table</li>
              <li>✓ Verify IP capture</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Error Monitoring:</h4>
            <ul className="space-y-1 text-muted-foreground">
              <li>✓ Trigger test error (button above)</li>
              <li>✓ Check Sentry dashboard</li>
              <li>✓ Verify error details</li>
              <li>✓ Test session replay</li>
            </ul>
          </div>
        </div>
      </MbCard>
    </div>
  );
}
